import {
  EstablishmentCheckout,
  isRestrictionAll,
  PaymentMethod,
  Product,
  RESTRICTION_ALL,
  ShippingOption,
  TypesOfShippings,
} from './types';
import { SHIPPING_OPTION_KEYS } from './shippingOptionsPaymentVisibility';
import { getPaymentMethods, getPaymentMethodValue } from './getActivePaymentMethods';

export type PurchaseRestrictable = Pick<
  Product,
  'allowedShippingOptions' | 'allowedPaymentMethods'
>;

export type ActivePurchaseOptions = {
  shippingTypes: TypesOfShippings[];
  paymentValues: string[];
};

export type PurchaseRestrictionDimension = 'shipping' | 'payment';

export type PurchaseRestrictionKind = PurchaseRestrictionDimension | 'both';

export type PurchaseOptions = {
  shippingOptions: { type: TypesOfShippings; name: string }[];
  paymentMethods: { value: string; name: string }[];
};

export type RestrictionSelection = {
  shippingType: TypesOfShippings | null;
  paymentValue: string | null;
};

export type PurchaseRestrictionViolation = {
  productId: string;
  productName: string;
  dimension: PurchaseRestrictionDimension;
};

export type PurchaseSelectionViolation = {
  productId: string;
  product: string;
  kind: PurchaseRestrictionKind;
  shipping: string[] | null;
  payment: string[] | null;
};

type IdentifiedRestrictable = PurchaseRestrictable & Pick<Product, 'id' | 'name'>;

export function formatRestrictionNames(names: string[], conjunction: string): string {
  if (names.length <= 1) {
    return names.join('');
  }
  return `${names.slice(0, -1).join(', ')} ${conjunction} ${names[names.length - 1]}`;
}

export function getActiveShippingOptions(
  checkout:
    | Partial<Pick<EstablishmentCheckout, (typeof SHIPPING_OPTION_KEYS)[number]>>
    | null
    | undefined,
): ShippingOption[] {
  if (!checkout) {
    return [];
  }

  return SHIPPING_OPTION_KEYS.map(key => checkout[key]).filter((option): option is ShippingOption =>
    isOfferedShippingOption(option),
  );
}

export function hasTableService(option: ShippingOption | null | undefined): boolean {
  return option?.type === TypesOfShippings.IN_PLACE && Boolean(option.tableService);
}

export function isOfferedShippingOption(option: ShippingOption | null | undefined): boolean {
  return Boolean(option?.active) && !hasTableService(option);
}

export function isTableServiceEnabled(
  checkout: Partial<Pick<EstablishmentCheckout, 'shippingOptionInPlace'>> | null | undefined,
): boolean {
  const option = checkout?.shippingOptionInPlace;

  return Boolean(option?.active) && hasTableService(option);
}

export function toCheckoutShippingType(type: TypesOfShippings): TypesOfShippings {
  return type === TypesOfShippings.PARCEL ? TypesOfShippings.DELIVERY : type;
}

export function toPurchaseOptions(
  checkout: EstablishmentCheckout,
  integrations: Parameters<typeof getPaymentMethods>[1],
): PurchaseOptions {
  return {
    shippingOptions: getActiveShippingOptions(checkout).map(option => ({
      type: option.type,
      name: option.name,
    })),
    paymentMethods: getPaymentMethods(checkout, integrations).map(paymentMethod => ({
      value: getPaymentMethodValue(paymentMethod),
      name: paymentMethod.name,
    })),
  };
}

export function toActivePurchaseOptions(
  options: PurchaseOptions | null | undefined,
): ActivePurchaseOptions {
  return {
    shippingTypes: options?.shippingOptions.map(option => option.type) ?? [],
    paymentValues: options?.paymentMethods.map(method => method.value) ?? [],
  };
}

export function toRestrictionSelection(
  source:
    | {
        shippingOption?: Pick<ShippingOption, 'type'> | null;
        paymentMethod?: Pick<PaymentMethod, 'type' | 'name'> | null;
      }
    | null
    | undefined,
): RestrictionSelection {
  return {
    shippingType: source?.shippingOption?.type ?? null,
    paymentValue: source?.paymentMethod ? getPaymentMethodValue(source.paymentMethod) : null,
  };
}

export function hasDeclaredPurchaseRestriction(item: PurchaseRestrictable): boolean {
  return (
    !isRestrictionAll(item.allowedShippingOptions) || !isRestrictionAll(item.allowedPaymentMethods)
  );
}

function getActiveValues(
  active: ActivePurchaseOptions,
  dimension: PurchaseRestrictionDimension,
): string[] {
  return dimension === 'shipping' ? active.shippingTypes : active.paymentValues;
}

function getEffectiveOptions(
  item: PurchaseRestrictable,
  active: ActivePurchaseOptions,
  dimension: PurchaseRestrictionDimension,
): string[] {
  const declared: readonly string[] | undefined =
    dimension === 'shipping' ? item.allowedShippingOptions : item.allowedPaymentMethods;
  const activeValues = getActiveValues(active, dimension);

  if (isRestrictionAll(declared)) {
    return activeValues;
  }
  const effective = activeValues.filter(value => declared.includes(value));

  return effective.length > 0 ? effective : activeValues;
}

function hasEffectiveRestriction(
  item: PurchaseRestrictable,
  active: ActivePurchaseOptions,
  dimension: PurchaseRestrictionDimension,
): boolean {
  return (
    getEffectiveOptions(item, active, dimension).length < getActiveValues(active, dimension).length
  );
}

function getAllowedOptions<T extends string>(
  items: PurchaseRestrictable[],
  active: ActivePurchaseOptions,
  dimension: PurchaseRestrictionDimension,
  activeValues: T[],
): T[] {
  return items.reduce<T[]>((allowed, item) => {
    const effective = new Set(getEffectiveOptions(item, active, dimension));

    return allowed.filter(value => effective.has(value));
  }, activeValues);
}

function getRestrictionConflictDimensions(
  items: PurchaseRestrictable[],
  active: ActivePurchaseOptions,
): PurchaseRestrictionDimension[] {
  const dimensions: PurchaseRestrictionDimension[] = ['shipping', 'payment'];

  return dimensions.filter(
    dimension =>
      getActiveValues(active, dimension).length > 0 &&
      getAllowedOptions(items, active, dimension, getActiveValues(active, dimension)).length === 0,
  );
}

function uniqueById<T extends Pick<Product, 'id'>>(items: T[]): T[] {
  const seen = new Set<string>();

  return items.filter(item => {
    if (seen.has(item.id)) {
      return false;
    }
    seen.add(item.id);

    return true;
  });
}

export function findRestrictionViolations(
  product: IdentifiedRestrictable,
  selection: RestrictionSelection,
  active: ActivePurchaseOptions,
): PurchaseRestrictionViolation[] {
  const violations: PurchaseRestrictionViolation[] = [];

  if (
    selection.shippingType &&
    !getEffectiveOptions(product, active, 'shipping').includes(selection.shippingType)
  ) {
    violations.push({ productId: product.id, productName: product.name, dimension: 'shipping' });
  }
  if (
    selection.paymentValue &&
    !getEffectiveOptions(product, active, 'payment').includes(selection.paymentValue)
  ) {
    violations.push({ productId: product.id, productName: product.name, dimension: 'payment' });
  }
  return violations;
}

export function getPurchaseRestrictionLabels(
  item: PurchaseRestrictable,
  options: PurchaseOptions | null | undefined,
  active: ActivePurchaseOptions = toActivePurchaseOptions(options),
): Record<PurchaseRestrictionDimension, string[] | null> {
  if (!options) {
    return { shipping: null, payment: null };
  }
  const shippingTypes = getEffectiveOptions(item, active, 'shipping');
  const paymentValues = getEffectiveOptions(item, active, 'payment');

  return {
    shipping:
      shippingTypes.length < active.shippingTypes.length
        ? uniqueNames(
            options.shippingOptions
              .filter(option => shippingTypes.includes(option.type))
              .map(option => option.name),
          )
        : null,
    payment:
      paymentValues.length < active.paymentValues.length
        ? uniqueNames(
            options.paymentMethods
              .filter(method => paymentValues.includes(method.value))
              .map(method => method.name),
          )
        : null,
  };
}

function uniqueNames(names: string[]): string[] {
  return Array.from(new Set(names));
}

function getAllowedPurchaseOptions(
  items: PurchaseRestrictable[],
  active: ActivePurchaseOptions,
): ActivePurchaseOptions {
  return {
    shippingTypes: getAllowedOptions(items, active, 'shipping', active.shippingTypes),
    paymentValues: getAllowedOptions(items, active, 'payment', active.paymentValues),
  };
}

function getUnavailableValues<T extends string>(active: T[], allowed: string[]): T[] {
  return allowed.length === 0 ? [] : active.filter(value => !allowed.includes(value));
}

export function getUnavailablePurchaseOptions(
  items: PurchaseRestrictable[],
  options: PurchaseOptions | null | undefined,
): ActivePurchaseOptions {
  const active = toActivePurchaseOptions(options);
  const allowed = getAllowedPurchaseOptions(items, active);

  return {
    shippingTypes: getUnavailableValues(active.shippingTypes, allowed.shippingTypes),
    paymentValues: getUnavailableValues(active.paymentValues, allowed.paymentValues),
  };
}

export function getAllowedPurchaseLabels(
  items: PurchaseRestrictable[],
  options: PurchaseOptions | null | undefined,
): Record<PurchaseRestrictionDimension, string[] | null> {
  const active = toActivePurchaseOptions(options);
  const allowed = getAllowedPurchaseOptions(items, active);
  const labels = getPurchaseRestrictionLabels(
    {
      allowedShippingOptions: allowed.shippingTypes,
      allowedPaymentMethods: allowed.paymentValues,
    },
    options,
    active,
  );

  return {
    shipping: allowed.shippingTypes.length > 0 ? labels.shipping : null,
    payment: allowed.paymentValues.length > 0 ? labels.payment : null,
  };
}

export function collapseToRestrictionAll<T extends string>(
  checked: T[],
  activeValues: readonly string[],
): T[] | [typeof RESTRICTION_ALL] {
  const checkedValues = new Set<string>(checked);
  const coversEveryActiveOption =
    activeValues.length > 0 &&
    !isRestrictionAll(checked) &&
    checkedValues.size === activeValues.length &&
    activeValues.every(value => checkedValues.has(value));

  return coversEveryActiveOption ? [RESTRICTION_ALL] : checked;
}

function toSelectionViolation(
  item: IdentifiedRestrictable,
  dimensions: PurchaseRestrictionDimension[],
  options: PurchaseOptions,
  active: ActivePurchaseOptions,
): PurchaseSelectionViolation[] {
  if (dimensions.length === 0) {
    return [];
  }
  const labels = getPurchaseRestrictionLabels(item, options, active);
  const shipping = dimensions.includes('shipping') ? (labels.shipping ?? []) : null;
  const payment = dimensions.includes('payment') ? (labels.payment ?? []) : null;

  return [
    {
      productId: item.id,
      product: item.name,
      kind: shipping && payment ? 'both' : shipping ? 'shipping' : 'payment',
      shipping,
      payment,
    },
  ];
}

export function getSelectionRestrictionViolations(
  items: IdentifiedRestrictable[],
  selection: RestrictionSelection,
  options: PurchaseOptions | null | undefined,
): PurchaseSelectionViolation[] {
  if (!options) {
    return [];
  }
  const active = toActivePurchaseOptions(options);

  return uniqueById(items).flatMap(item =>
    toSelectionViolation(
      item,
      findRestrictionViolations(item, selection, active)
        .map(violation => violation.dimension)
        .filter(dimension => hasEffectiveRestriction(item, active, dimension)),
      options,
      active,
    ),
  );
}

export function getRestrictionConflictViolations(
  items: IdentifiedRestrictable[],
  options: PurchaseOptions | null | undefined,
): PurchaseSelectionViolation[] {
  if (!options) {
    return [];
  }
  const active = toActivePurchaseOptions(options);
  const uniqueItems = uniqueById(items);
  const dimensions = getRestrictionConflictDimensions(uniqueItems, active);

  return uniqueItems.flatMap(item =>
    toSelectionViolation(
      item,
      dimensions.filter(dimension => hasEffectiveRestriction(item, active, dimension)),
      options,
      active,
    ),
  );
}
